import React, { useState } from "react";
import api from "../../connector/api";

const REQUIRED_COLUMNS = ["username", "email", "first_name", "last_name", "password"];

const BulkUploadModal = ({ isOpen, onClose, onUsersUploaded }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  if (!isOpen) return null;

  // Parse CSV for preview and validate required fields
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setErrorMsg(null);
    setSuccessMsg(null);
    setPreview([]);

    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter(Boolean).slice(0, 6);
      const headers = lines[0].split(",").map((h) => h.trim());

      // Check if required columns exist
      const missingColumns = REQUIRED_COLUMNS.filter(
        (col) => !headers.includes(col)
      );
      if (missingColumns.length > 0) {
        setErrorMsg(
          `CSV is missing required columns: ${missingColumns.join(", ")}`
        );
        return;
      }

      // Map rows
      
      const previewRows = lines.slice(1).map((line) => {
        const values = line.split(",").map((v) => v.trim());
        const obj = {};
        headers.forEach((header, idx) => {
          obj[header] = values[idx]?.trim() || "";
        });
        return obj;
      });

      // Mark rows with missing username
      previewRows.forEach((row) => {
        row._invalid = !row.username; // custom flag for invalid rows
      });

      setPreview(previewRows);
    };
    reader.readAsText(selectedFile);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setErrorMsg("Please select a CSV file");
      return;
    }

    // Check for invalid rows
    const invalidRows = preview.filter((row) => row._invalid);
    if (invalidRows.length > 0) {
      setErrorMsg(
        `Cannot upload. ${invalidRows.length} row(s) missing username.`
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const res = await api.post("/users/bulk-upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { created, errors } = res.data;

      if (errors && errors.length > 0) {
        setErrorMsg(
          `Some users could not be created:\n${errors
            .map((e) => `${e.username || "unknown"}: ${e.error}`)
            .join("\n")}`
        );
      }

      if (created && created.length > 0) {
        setSuccessMsg(`Successfully created users: ${created.join(", ")}`);
        onUsersUploaded(created);
        setFile(null);
        setPreview([]);
        setTimeout(() => {
          onClose();
        }, 800);
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      setErrorMsg(
        err.response?.data?.error || "Something went wrong while uploading"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white w-full max-w-md rounded shadow-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Bulk Upload Users
        </h2>

        <form onSubmit={handleUpload} className="space-y-3">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="w-full text-sm"
          />
          <p className="text-xs text-gray-500">
            Upload a CSV file with columns:{" "}
            <b>username, email, first_name, last_name, password</b>
          </p>

          {preview.length > 0 && (
            <div className="border p-2 text-xs max-h-40 overflow-y-auto">
              <p className="font-semibold mb-1">Preview (first 5 rows):</p>
              <table className="w-full table-auto text-left text-xs">
                <thead>
                  <tr>
                    {Object.keys(preview[0])
                      .filter((key) => key !== "_invalid")
                      .map((header) => (
                        <th key={header} className="px-1">
                          {header}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, idx) => (
                    <tr
                      key={idx}
                      className={row._invalid ? "bg-red-100 text-red-600" : ""}
                    >
                      {Object.keys(row)
                        .filter((key) => key !== "_invalid")
                        .map((key, i) => (
                          <td key={i} className="px-1">
                            {row[key]}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {errorMsg && (
            <div className="text-red-600 text-sm whitespace-pre-wrap">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="text-green-600 text-sm whitespace-pre-wrap">
              {successMsg}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded bg-[#f7b512] text-white hover:bg-yellow-700"
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkUploadModal;
